/**
 * @description
 * SubstitutePaymentDialogComponent provides a modal dialog to rectify an erroneous
 * payment method on a finalized/paid sales order in compliance with French tax regulations
 * (BOFIP BOI-TVA-DECLA-30-10-30 and NF525/LNE standards).
 *
 * It enforces:
 * - 1:1 strict amount substitution (identical amount)
 * - Required reason / justification (minimum 5 characters) for audit trail
 * - Selection of a different, active payment method from c_values category 7
 * - Conditional metadata input (check number, bank name, card reference)
 *
 * Called via: MatDialog.open(SubstitutePaymentDialogComponent, { data: { orderId, originalPayment } })
 *
 * Dependencies:
 * - QuotationService (`@app/sales/quotation/quotation.service`)
 * - ValuesService (`@app/common/values/values.service`)
 * - FormatCurrencyPipe (`@app/shared/pipes/format-currency.pipe`)
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { catchError, finalize, of } from 'rxjs';
import Swal from 'sweetalert2';

import { ValuesService } from '@app/common/values/values.service';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { C_PAYMENT_METHOD } from '@app/shared/constants';
import { QuotationService } from '../quotation.service';

export interface SubstitutePaymentDialogData {
  orderId: number;
  originalPayment: {
    id: number;
    method: number;
    amount: number;
    methodLabel?: string;
    comment?: string;
  };
}

@Component({
  selector: 'app-substitute-payment-dialog',
  standalone: true,
  templateUrl: './substitute-payment-dialog.component.html',
  styleUrls: ['./substitute-payment-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    FormatCurrencyPipe,
  ],
})
export class SubstitutePaymentDialogComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private quotationService = inject(QuotationService);
  private valuesService = inject(ValuesService);
  private cdr = inject(ChangeDetectorRef);

  public isSubmitting = false;
  public errorMessage: string | null = null;
  public paymentMethods: { id: number; name: string }[] = [];

  public formGroup = new FormGroup({
    newMethod: new FormControl<number | null>(null, [Validators.required]),
    reason: new FormControl<string>('', [Validators.required, Validators.minLength(5)]),
    checkNumber: new FormControl<string>(''),
    bankName: new FormControl<string>(''),
    cardRef: new FormControl<string>(''),
    transferRef: new FormControl<string>(''),
  });

  constructor(
    public dialogRef: MatDialogRef<SubstitutePaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SubstitutePaymentDialogData,
  ) {}

  ngOnInit(): void {
    this.loadPaymentMethods();

    this.formGroup
      .get('newMethod')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateConditionalValidators();
        this.cdr.markForCheck();
      });
  }

  get originalPayment() {
    return this.data.originalPayment;
  }

  get selectedMethodId(): number {
    return Number(this.formGroup.get('newMethod')?.value || 0);
  }

  private loadPaymentMethods(): void {
    this.valuesService
      .getValues$(C_PAYMENT_METHOD)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: any[]) => {
        const origMethod = this.data.originalPayment.method;
        this.paymentMethods = values.filter(
          v => v.id > 0 && v.is_active !== false && v.id !== 4 && v.id !== origMethod,
        );
        if (this.paymentMethods.length > 0) {
          this.formGroup.get('newMethod')?.setValue(this.paymentMethods[0].id);
        }
        this.cdr.markForCheck();
      });
  }

  private updateConditionalValidators(): void {
    const method = this.selectedMethodId;
    const checkCtrl = this.formGroup.get('checkNumber');
    const bankCtrl = this.formGroup.get('bankName');

    if (method === 3) {
      // Check
      checkCtrl?.setValidators([Validators.required]);
    } else {
      checkCtrl?.clearValidators();
      bankCtrl?.clearValidators();
    }
    checkCtrl?.updateValueAndValidity();
    bankCtrl?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.formGroup.invalid || this.isSubmitting) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const newMethod = Number(this.formGroup.get('newMethod')?.value);
    const reason = this.formGroup.get('reason')?.value?.trim() || '';

    const paymentData: any = {};
    const checkNum = this.formGroup.get('checkNumber')?.value?.trim();
    const bank = this.formGroup.get('bankName')?.value?.trim();
    const card = this.formGroup.get('cardRef')?.value?.trim();
    const transfer = this.formGroup.get('transferRef')?.value?.trim();

    if (newMethod === 3) {
      if (checkNum) paymentData.check_number = checkNum;
      if (bank) paymentData.bank = bank;
    } else if (newMethod === 1) {
      if (card) paymentData.card_ref = card;
    } else if (newMethod === 6) {
      if (transfer) paymentData.transfer_ref = transfer;
    }

    this.isSubmitting = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.quotationService
      .substitutePayment(this.data.orderId, this.data.originalPayment.id, newMethod, reason, paymentData)
      .pipe(
        catchError(err => {
          const msg = err?.error?.error || err?.message || 'Erreur lors de la substitution du règlement.';
          this.errorMessage = msg;
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(res => {
        if (res && res.success) {
          this.dialogRef.close({ substituted: true, response: res });
        }
      });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
